<template>
  <form class="form-survey-section" @submit.prevent="submitOpinion">
    <h3 class="question">
      {{ isPassed ? thanks : question }}
    </h3>
    <div v-if="!isPassed" class="stars" @mouseleave="hoverValue = 0">
      <div class="rating">
        <svg
          v-for="starIndex in 5"
          :key="starIndex"
          class="star"
          :class="{ filled: starIndex <= (hoverValue || answer.score) }"
          width="24"
          height="22"
          viewBox="0 0 34 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          @mouseenter="hoverValue = starIndex"
          @click="setRating(starIndex)"
        >
          <path
            fill="currentColor"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M31.3502 9.9194L24.0134 8.8687C23.5982 8.81715 23.2368 8.55936 23.0453 8.17891L19.7741 1.67205C19.7688 1.66494 19.7652 1.65783 19.7616 1.64894C19.461 1.09604 18.9939 0.632024 18.4303 0.335127C16.9146 -0.431117 15.0339 0.159122 14.234 1.67205L10.9593 8.17713C10.766 8.55758 10.401 8.81715 9.96973 8.87048L2.64902 9.9194C1.94039 10.0243 1.31945 10.3372 0.857763 10.8207C0.288713 11.4056 -0.015497 12.1772 0.000608203 12.9915C0.0167134 13.8057 0.351344 14.5613 0.938289 15.1195L6.24764 20.197C6.5429 20.4708 6.68069 20.8886 6.60911 21.2957L5.35469 28.439C5.24733 29.1057 5.36185 29.7937 5.67322 30.3626C6.06332 31.0915 6.7129 31.6231 7.50384 31.8649C7.80269 31.9556 8.10868 32 8.4111 32C8.90858 32 9.40068 31.8791 9.84626 31.6427L16.3956 28.279C16.775 28.0781 17.2313 28.0781 17.6196 28.2826L24.1369 31.632C24.722 31.952 25.3895 32.0676 26.0928 31.9609C27.7731 31.6871 28.9237 30.1048 28.6499 28.4248L27.3973 21.2957C27.3239 20.8761 27.4599 20.4654 27.7695 20.1703L33.0627 15.1195C33.5584 14.6448 33.8841 14.0101 33.9736 13.3328C34.1937 11.6759 33.0144 10.1434 31.3502 9.9194Z"
          />
        </svg>
      </div>
      <p class="rating-name" :class="{ flat: !answer.score }">
        {{ ratingName || '' }}
      </p>
    </div>
    <div class="comment-section" :class="{ open: !!answer.score }">
      <div class="comment">
        <Textarea
          v-model="answer.comment"
          class="textarea"
          rows="6"
          theme="white"
          label="Ваши замечания и предложения. Что бы вы хотели видеть в будущем?"
          :max-length="SURVEY_COMMENT_LIMIT"
        />
        <TextCounter :count="answer.comment?.length || 0" :limit="SURVEY_COMMENT_LIMIT" />
      </div>
      <Button :disabled="answer.score === 0" type="submit" name="Оценить" :wide="true" />
    </div>
    <Loader v-if="isLoading" />
  </form>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useInit } from '~/composables/use-init';
import { useSurveyScore } from '~/composables/use-survey-score';

import { SurveySectionScore } from '~/models/SurveySectionModel';

import { useUserStore } from '~/stores/use-user.store';

import Button from '~/components/controls/Button.vue';
import Loader from '~/components/components/loader/Loader.client.vue';
import Textarea from '~/components/controls/Textarea.vue';
import TextCounter from '~/components/components/text/TextCounter.vue';

const init = useInit();
const route = useRoute();
const survey = useSurveyScore();

const userStore = useUserStore();

const emit = defineEmits(['on-score']);

defineProps({
  question: {
    type: String,
    required: true,
  },
  thanks: {
    type: String,
    default: 'Спасибо за оценку!',
  },
});

const SURVEY_COMMENT_LIMIT = 300;

const hoverValue: Ref<number> = ref(0);
const isLoading: Ref<boolean> = ref(false);
const isPassed: Ref<boolean> = ref(false);
const answer: Ref<SurveySectionScore> = ref({
  user_id: null,
  score: 0,
  comment: '',
  section: '',
});

const setRating = (value: number) => {
  answer.value.score = value;
};

const ratingName = computed(() => {
  const activeRating = hoverValue.value || answer.value.score;

  switch (activeRating) {
    case 1:
      return 'Плохо';
    case 2:
      return 'Удовлетворительно';
    case 3:
      return 'Средне';
    case 4:
      return 'Хорошо';
    case 5:
      return 'Отлично!';
    default:
      return '';
  }
});

async function submitOpinion(): Promise<void> {
  if (isLoading.value) return;
  answer.value.user_id = init.isUserAuthenticated.value ? userStore.getUser.id : null;
  answer.value.section = route.path;

  try {
    isLoading.value = true;
    if (!(await survey.setScore(answer.value))) return;
    isPassed.value = true;
    emit('on-score', answer.value);
  } catch {
  } finally {
    isLoading.value = false;
  }

  clear();
}

function clear(): void {
  answer.value.score = 0;
  answer.value.comment = '';
  hoverValue.value = 0;
}
</script>

<style lang="scss" scoped>
.form-survey-section {
  display: grid;
  grid-auto-flow: row;
  place-items: center;
  gap: 20px;
  width: 400px;
  background-color: $color-secondary-bg;
  padding: 32px 16px;
  border-radius: $border-radius-l;

  @include mobile {
    border-radius: unset;
    width: 100%;
    background-color: #fff;
    gap: 12px;
  }

  > .question {
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
    max-width: 300px;

    @include mobile {
      font-size: 20px;
      line-height: 28px;
    }
  }

  > .stars {
    display: grid;
    grid-auto-flow: row;
    place-items: center;
    gap: 12px;

    @include mobile {
      gap: 8px;
    }
  }

  > .stars > .rating {
    display: grid;
    cursor: pointer;
    user-select: none;
    grid-auto-flow: column;
    gap: 20px;
    -webkit-tap-highlight-color: transparent;
  }

  > .stars > .rating > .star {
    color: $color-tertiary-content;
    transition: color 0.2s ease-in-out;

    &.filled {
      color: #ffcb05;
    }
  }

  > .stars > .rating-name {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: $color-potato-50;

    &.flat {
      margin-bottom: -24px;

      @media (hover: hover) {
        margin-top: -14px;
      }
    }
  }

  > .comment-section {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    flex-direction: column;
    width: 100%;
    opacity: 0;
    max-height: 0;
    height: 0;
    overflow: hidden;
    transition:
      max-height $transition,
      opacity $transition;

    &.open {
      opacity: 1;
      max-height: 500px;
      height: unset;
    }
  }

  > .comment-section > .comment {
    width: 100%;
  }
}
</style>
